import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
    ChevronDown, ExternalLink, Star, CheckCircle, XCircle, Trash2, Eye, EyeOff, Tags,
} from 'lucide-react';
import type { AcademyVideoItem } from '../types';
import { STATUS_MAP, shortDate } from '../constants';

function parseJsonArray(val: unknown): string[] {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter((x): x is string => typeof x === 'string');
    if (typeof val === 'string') {
        try {
            const arr = JSON.parse(val);
            return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
        } catch { return []; }
    }
    return [];
}

interface AcademyVideoCardProps {
    video: AcademyVideoItem;
    isExpanded: boolean;
    onToggle: () => void;
    onApprove: () => void;
    onReject: () => void;
    onDelete: () => void;
    onToggleHidden?: () => void;
    onEditCategories?: () => void;
    isActioning: boolean;
    /** En vista compacta (ej. bajo creador), siempre muestra acciones sin expandir */
    compact?: boolean;
    /** Modo selección múltiple: mostrar checkbox para rechazar varios */
    selectable?: boolean;
    selected?: boolean;
    onSelectChange?: (checked: boolean) => void;
}

export function AcademyVideoCard({
    video,
    isExpanded,
    onToggle,
    onApprove,
    onReject,
    onDelete,
    onToggleHidden,
    onEditCategories,
    isActioning,
    compact = false,
    selectable = false,
    selected = false,
    onSelectChange,
}: AcademyVideoCardProps) {
    const showDetails = isExpanded || compact;
    const statusCfg = STATUS_MAP[video.status] || STATUS_MAP.pending;
    const createdDate = new Date(video.created_at);
    const thumbUrl = video.thumbnail_url || `https://img.youtube.com/vi/${extractYouTubeId(video.video_url)}/mqdefault.jpg`;

    return (
        <div
            className={cn(
                'rounded-xl border bg-card transition-all',
                video.status === 'pending' ? 'border-amber-500/30' : 'border-border',
                isExpanded && 'ring-1 ring-primary/20',
                selectable && selected && 'ring-1 ring-primary'
            )}
        >
            <div
                className={cn(
                    'flex items-center gap-4 px-4 py-3 transition-colors',
                    !compact && !selectable && 'cursor-pointer hover:bg-muted/20'
                )}
                onClick={compact || selectable ? undefined : onToggle}
            >
                {selectable && (
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => onSelectChange?.(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 shrink-0 rounded border-border accent-primary cursor-pointer"
                        aria-label={selected ? 'Deseleccionar vídeo' : 'Seleccionar vídeo para rechazar'}
                    />
                )}
                <div className="w-24 h-14 rounded-lg overflow-hidden bg-muted/50 shrink-0 border border-border/50">
                    <img
                        src={thumbUrl}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                        onError={e => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="54" fill="%23333"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="10">Sin miniatura</text></svg>';
                        }}
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-foreground truncate max-w-lg">
                            {video.title}
                        </p>
                        <StatusBadge status={statusCfg.label} variant={statusCfg.variant} />
                        {video.hidden && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/20 flex items-center gap-1">
                                <EyeOff size={10} />
                                Oculto
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span>{video.creator_display_name}</span>
                        <span className="opacity-40">·</span>
                        {video.duration_seconds && (
                            <>
                                <span>{formatDuration(video.duration_seconds)}</span>
                                <span className="opacity-40">·</span>
                            </>
                        )}
                        {video.average_rating != null && (
                            <>
                                <span className="flex items-center gap-0.5">
                                    <Star size={10} className="fill-amber-400 text-amber-400" />
                                    {video.average_rating}
                                </span>
                                <span className="opacity-40">·</span>
                            </>
                        )}
                        <span>{video.ratings_count} valoraciones</span>
                        <span className="opacity-40">·</span>
                        <span>{shortDate(createdDate)}</span>
                    </div>
                </div>

                {!compact && (
                    <ChevronDown
                        size={16}
                        className={cn(
                            'text-muted-foreground transition-transform shrink-0',
                            isExpanded && 'rotate-180'
                        )}
                    />
                )}
            </div>

            {showDetails && (
                <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-4">
                    {(() => {
                        const mods = parseJsonArray(video.modalities);
                        const locs = parseJsonArray(video.location_types);
                        const specs = parseJsonArray(video.species);
                        const hasCats = mods.length > 0 || locs.length > 0 || specs.length > 0;
                        return hasCats ? (
                            <div className="flex flex-wrap gap-1.5">
                                {mods.map((m) => (
                                    <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                                        {m}
                                    </span>
                                ))}
                                {locs.map((l) => (
                                    <span key={l} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
                                        {l}
                                    </span>
                                ))}
                                {specs.slice(0, 3).map((s) => (
                                    <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                                        {s}
                                    </span>
                                ))}
                                {specs.length > 3 && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                                        +{specs.length - 3}
                                    </span>
                                )}
                            </div>
                        ) : null;
                    })()}
                    {video.description && (
                        <div className="rounded-lg bg-muted/30 p-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descripción</p>
                            <p className="text-sm text-foreground line-clamp-4">{video.description}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                        {onEditCategories && (
                            <button
                                onClick={e => { e.stopPropagation(); onEditCategories(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                            >
                                <Tags size={14} />
                                Editar categorías
                            </button>
                        )}
                        <a
                            href={video.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            onClick={e => e.stopPropagation()}
                        >
                            <ExternalLink size={14} />
                            Ver en YouTube
                        </a>

                        {video.status !== 'approved' && (
                            <button
                                onClick={e => { e.stopPropagation(); onApprove(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle size={14} />
                                Aprobar
                            </button>
                        )}

                        {video.status !== 'rejected' && (
                            <button
                                onClick={e => { e.stopPropagation(); onReject(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                            >
                                <XCircle size={14} />
                                Rechazar
                            </button>
                        )}

                        {onToggleHidden && video.status === 'approved' && (
                            <button
                                onClick={e => { e.stopPropagation(); onToggleHidden(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                                title={video.hidden ? 'Mostrar en la app' : 'Ocultar en la app (sigue en BD para sincronización)'}
                            >
                                {video.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                                {video.hidden ? 'Mostrar' : 'Ocultar'}
                            </button>
                        )}

                        <button
                            onClick={e => { e.stopPropagation(); onDelete(); }}
                            disabled={isActioning}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50 ml-auto"
                        >
                            <Trash2 size={14} />
                            Eliminar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function extractYouTubeId(url: string): string {
    const m = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : '';
}

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}
